import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import {
  PlayCircle, FileText, Image as ImageIcon, CheckCircle2, Clock, Video,
  Award, Eye,
} from 'lucide-react';

import { getMyTraining, startQuizAttempt } from '../../services/trainingService';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import DocumentViewerModal from '../../components/ui/DocumentViewerModal';
import { formatDate } from '../../utils/Utils';
import ContentPlayer from './player/ContentPlayer';
import EvaluationModal from './EvaluationModal';

const Container = styled.div`padding: 24px; max-width: 1200px; margin: 0 auto;`;

const Hero = styled.div`
  background: ${({ $src, theme }) => $src
    ? `linear-gradient(135deg, rgba(0,0,0,0.45), rgba(0,0,0,0.25)), url(${$src}) center/cover no-repeat`
    : `linear-gradient(135deg, ${theme.colors.primary.main}, ${theme.colors.secondary.main})`};
  border-radius: 8px;
  padding: 32px 24px;
  color: #fff;
  margin-bottom: 20px;
`;

const HeroTitle = styled.h1`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 26px;
  font-weight: 800;
  margin: 0 0 8px;
`;

const HeroMeta = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  opacity: 0.9;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 20px;
  @media (max-width: 1000px) { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const SectionTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 16px;
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1.5px solid ${({ $active, $done, theme }) => (
    $active ? theme.colors.primary.main : $done ? theme.colors.secondary.main : '#E0E4E8'
  )};
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  background: ${({ $active }) => ($active ? '#EFF6FF' : '#fff')};
  transition: border-color 0.15s, background 0.15s;
`;

const ItemIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 6px;
  background: ${({ $done }) => ($done ? '#D1FAE5' : '#EEF2FF')};
  color: ${({ $done, theme }) => ($done ? '#065F46' : theme.colors.primary.main)};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ItemMeta = styled.div`flex: 1; min-width: 0;`;

const ItemName = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemSub = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #F1F5F9;
  font-size: 13px;
  &:last-child { border-bottom: none; }
`;

const MetaLabel = styled.span`color: #64748b;`;
const MetaValue = styled.span`font-weight: 600; color: ${({ theme }) => theme.colors.text.primary};`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 6px;
  background: #E2E8F0;
  border-radius: 3px;
  overflow: hidden;
  margin: 4px 0 12px;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => `${$pct}%`};
  background: ${({ theme }) => theme.colors.primary.main};
  transition: width 0.3s;
`;

const QuizCta = styled.div`
  background: #F0FDF4;
  border: 1px solid #86EFAC;
  border-radius: 6px;
  padding: 14px;
  margin-top: 12px;
`;

const CONTENT_ICON = {
  video: Video,
  youtube: PlayCircle,
  vimeo: PlayCircle,
  pdf: FileText,
  pptx: FileText,
  image: ImageIcon,
};

export default function MyTrainingViewPage() {
  const { t, i18n } = useTranslation();
  const { assignmentId } = useParams();
  const history = useHistory();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [startingQuiz, setStartingQuiz] = useState(false);
  const [evalOpen, setEvalOpen] = useState(false);
  const [evalOffered, setEvalOffered] = useState(false);
  // Sertifika PDF'i özel sınıfa taşındı (StorageVisibility.Private) — kayıtlı adres artık
  // doğrudan bir <a href> ile açılamaz. DocumentViewerModal kayıtlı adresi kendisi
  // `/v1/storage/view` üzerinden çözer (beş özlük-dosyası yüzeyiyle aynı desen).
  const [certViewerOpen, setCertViewerOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyTraining(assignmentId);
      const d = res.data?.data;
      setData(d);
      // Auto-select first incomplete item (or first item if all done).
      if (d?.contentItems?.length) {
        const firstIncomplete = d.contentItems.find((ci) => !ci.isCompleted);
        setActiveId((prev) => prev || firstIncomplete?.id || d.contentItems[0].id);
      }
      if (d?.status === 'completed' && !d?.evaluationSubmitted && !evalOffered) {
        setEvalOpen(true);
        setEvalOffered(true);
      }
    } finally {
      setLoading(false);
    }
  }, [assignmentId, evalOffered]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const activeItem = useMemo(
    () => (data?.contentItems || []).find((ci) => ci.id === activeId) || null,
    [data, activeId]
  );

  const allItemsDone = useMemo(
    () => !!data && data.contentItems.length > 0
      && data.contentItems.every((ci) => ci.isCompleted),
    [data]
  );

  const handleProgress = useCallback((res) => {
    if (!res) return;
    // Mutate the local copy with the latest completion flags so the sidebar
    // reflects reality without a refetch.
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        status: res.assignmentStatus || prev.status,
        contentItems: prev.contentItems.map((ci) => (
          ci.id === activeId
            ? {
              ...ci,
              isCompleted: res.isCompleted || ci.isCompleted,
              watchedSeconds: res.watchedSeconds ?? ci.watchedSeconds,
              viewedPages: res.viewedPages?.length ? res.viewedPages : ci.viewedPages,
            }
            : ci
        )),
        completedContentItemCount: res.completedContentItemCount ?? prev.completedContentItemCount,
        progressPercentage: res.contentItemCount > 0
          ? Math.round((res.completedContentItemCount / res.contentItemCount) * 100)
          : prev.progressPercentage,
      };
    });
  }, [activeId]);

  const onItemCompleted = useCallback(() => {
    // Full refresh so quiz eligibility, assignment status, and evaluation
    // prompt pick up the change.
    fetchData();
  }, [fetchData]);

  const beginQuiz = async () => {
    if (startingQuiz) return;
    setStartingQuiz(true);
    try {
      const res = await startQuizAttempt(assignmentId);
      const attempt = res?.data?.data;
      if (attempt?.attemptId) {
        history.push(`/my-trainings/${assignmentId}/quiz/${attempt.attemptId}`);
      }
    } finally {
      setStartingQuiz(false);
    }
  };

  if (loading) return <Container>{t('common.loading')}</Container>;
  if (!data) return null;

  const progress = data.progressPercentage ?? 0;
  const canTakeQuiz = data.hasQuiz && allItemsDone && !data.quizPassed;
  // Effective cap honors admin-granted bonus attempts. When server provides
  // effectiveMaxAttempts use it; fall back to raw maxAttempts for older payloads.
  const effectiveMax = data.effectiveMaxAttempts ?? data.quiz?.maxAttempts ?? 0;
  const quizMaxedOut = data.hasQuiz && effectiveMax > 0
    && (data.quizAttemptCount ?? 0) >= effectiveMax && !data.quizPassed;
  const remainingAttempts = effectiveMax > 0
    ? Math.max(0, effectiveMax - (data.quizAttemptCount ?? 0))
    : null;

  return (
    <Container>
      <Breadcrumbs items={[
        { label: t('training.myTrainings'), path: '/my-trainings' },
        { label: data.trainingTitle },
      ]} />

      <Hero $src={data.coverImageUrl}>
        <HeroTitle>{data.trainingTitle}</HeroTitle>
        <HeroMeta>
          {data.categoryName && <span>{data.categoryName}</span>}
          <span><Clock size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />{data.durationMinutes} {t('training.minutesShort')}</span>
          <span>{t('training.dueDate')}: {formatDate(data.dueDate, i18n.language)}</span>
          {data.status === 'completed' && (
            <span><CheckCircle2 size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />{t('training.assignmentStatus.completed')}</span>
          )}
        </HeroMeta>
      </Hero>

      <Columns>
        <div>
          {activeItem ? (
            <Card>
              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{t('training.contentItem')}</div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>
                    {activeItem.originalFileName || `${activeItem.contentType.toUpperCase()} #${activeItem.orderIndex + 1}`}
                  </div>
                </div>
                {activeItem.isCompleted && (
                  <span style={{ color: '#16A34A', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                    <CheckCircle2 size={16} /> {t('training.assignmentStatus.completed')}
                  </span>
                )}
              </div>
              <ContentPlayer
                key={activeItem.id}
                contentItem={activeItem}
                assignmentId={assignmentId}
                disableFastForward={data.disableFastForward}
                onCompleted={onItemCompleted}
                onProgress={handleProgress}
              />
            </Card>
          ) : (
            <Card>{t('training.noContentYet')}</Card>
          )}

          {data.hasQuiz && (
            <QuizCta>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                {data.quizPassed ? t('training.quizAlreadyPassedTitle')
                  : quizMaxedOut ? t('training.quizMaxAttemptsTitle')
                  : canTakeQuiz ? t('training.quizReadyTitle')
                  : t('training.quizLockedTitle')}
              </div>
              <div style={{ fontSize: 13, color: '#475569', marginBottom: 10 }}>
                {data.quizPassed ? t('training.quizAlreadyPassedBody')
                  : quizMaxedOut ? t('training.quizMaxAttemptsBody')
                  : canTakeQuiz ? t('training.quizReadyBody')
                  : t('training.quizLockedBody')}
              </div>
              {!data.quizPassed && remainingAttempts != null && !quizMaxedOut && (
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
                  {t('training.remainingAttempts', { n: remainingAttempts })}
                  {data.bonusAttempts > 0 && ` · ${t('training.bonusAttemptsGranted', { n: data.bonusAttempts })}`}
                </div>
              )}
              <Button
                type="button"
                $variant="primary"
                disabled={!canTakeQuiz || quizMaxedOut || startingQuiz}
                onClick={beginQuiz}
              >
                {t('training.startQuiz')}
              </Button>
            </QuizCta>
          )}
        </div>

        <div>
          <Card style={{ marginBottom: 16 }}>
            <SectionTitle>{t('training.contentItems')} ({data.contentItems.length})</SectionTitle>
            {data.contentItems.length === 0 ? (
              <div style={{ color: '#A0A8B4', fontSize: 13 }}>{t('training.noContentYet')}</div>
            ) : data.contentItems.map((ci) => {
              const Icon = CONTENT_ICON[ci.contentType] || FileText;
              return (
                <ItemRow
                  key={ci.id}
                  $active={ci.id === activeId}
                  $done={ci.isCompleted}
                  onClick={() => setActiveId(ci.id)}
                >
                  <ItemIcon $done={ci.isCompleted}>
                    {ci.isCompleted ? <CheckCircle2 size={18} /> : <Icon size={18} />}
                  </ItemIcon>
                  <ItemMeta>
                    <ItemName>{ci.originalFileName || `${ci.contentType.toUpperCase()} #${ci.orderIndex + 1}`}</ItemName>
                    <ItemSub>
                      {ci.durationSeconds
                        ? `${Math.round(ci.durationSeconds / 60)} ${t('training.minutesShort')}`
                        : ci.pageCount
                          ? `${ci.pageCount} ${t('training.pages')}`
                          : ci.contentType}
                    </ItemSub>
                  </ItemMeta>
                </ItemRow>
              );
            })}
          </Card>

          {data.status === 'completed' && data.certificatePdfUrl && (
            <Card style={{ marginBottom: 16, border: '1.5px solid #FBBF24', background: '#FFFBEB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <Award size={24} color="#B45309" />
                <div>
                  <div style={{ fontWeight: 800, color: '#B45309' }}>{t('training.certificateReady')}</div>
                  <div style={{ fontSize: 12, color: '#92400E' }}>{data.certificateNumber}</div>
                </div>
              </div>
              <div style={{ fontSize: 12, color: '#78350F', marginBottom: 10 }}>
                {t('training.certificateIssuedAt')}: {formatDate(data.certificateIssuedAt, i18n.language)}
              </div>
              <Button
                type="button"
                data-testid="certificate-view-trigger"
                $variant="primary"
                $fullWidth
                onClick={() => setCertViewerOpen(true)}
              >
                <Eye size={16} /> {t('training.viewCertificate')}
              </Button>
            </Card>
          )}

          <Card>
            <SectionTitle>{t('training.summary')}</SectionTitle>
            <MetaRow>
              <MetaLabel>{t('training.progress')}</MetaLabel>
              <MetaValue>%{progress}</MetaValue>
            </MetaRow>
            <ProgressTrack><ProgressFill $pct={progress} /></ProgressTrack>
            <MetaRow><MetaLabel>{t('training.startDate')}</MetaLabel><MetaValue>{formatDate(data.startDate, i18n.language)}</MetaValue></MetaRow>
            <MetaRow><MetaLabel>{t('training.dueDate')}</MetaLabel><MetaValue>{formatDate(data.dueDate, i18n.language)}</MetaValue></MetaRow>
            {data.hasQuiz && data.quiz && (
              <>
                <MetaRow><MetaLabel>{t('training.quizPassingScore')}</MetaLabel><MetaValue>%{data.quiz.passingScore}</MetaValue></MetaRow>
                <MetaRow>
                  <MetaLabel>{t('training.quizAttemptsUsed')}</MetaLabel>
                  <MetaValue>
                    {data.quizAttemptCount ?? 0}{data.quiz.maxAttempts > 0 ? ` / ${data.quiz.maxAttempts}` : ''}
                  </MetaValue>
                </MetaRow>
                <MetaRow>
                  <MetaLabel>{t('training.quizPassed')}</MetaLabel>
                  <MetaValue>{data.quizPassed ? '✓' : '–'}</MetaValue>
                </MetaRow>
              </>
            )}
          </Card>
        </div>
      </Columns>

      {evalOpen && (
        <EvaluationModal
          assignmentId={assignmentId}
          onClose={() => setEvalOpen(false)}
          onSubmitted={() => { setEvalOpen(false); fetchData(); }}
        />
      )}

      <DocumentViewerModal
        open={certViewerOpen}
        fileUrl={data.certificatePdfUrl}
        title={data.certificateNumber}
        onClose={() => setCertViewerOpen(false)}
      />
    </Container>
  );
}
